import loadable from '@loadable/component';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import not_configured_icon from '../../../assets/img/settings--tools.svg';
import { RssFeedItem, WidgetData, WidgetEndpoint } from '../../../models/Index';
import WidgetService from '../../../services/widgetService/WidgetService';
import BlockUi from '../../block-ui/BlockUi';
import DemoPreview from '../../demo-preview/DemoPreview';
import { useStyles } from './styles';

export const Marquee = loadable(() => import('react-fast-marquee'));

type Props = {
    data: WidgetData;
    id: string;
};

function RssFeed(props: Props) {
    const classes = useStyles();
    const [widgetRssFeedConfig, setWidgetRssFeedConfig] = useState({ link: '' } as WidgetEndpoint);
    const [widgetRssFeedData, setWidgetRssFeedData] = useState([] as RssFeedItem[]);
    const [tickerTextSpeed, setTickerTextSpeed] = useState(500);
    const [tickerFontSize, setTickerFontSize] = useState('1.6rem');
    const [requestInterval, setRequestInterval] = useState(null);
    const [link, setLink] = useState('');
    const [triggerRequest, setTriggerRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [requestCount, setRequestCount] = useState(0);
    const [backgroundColor, setBackgroundColor] = useState('');
    const [gradientColor, setGradientColor] = useState({ r: 255, g: 255, b: 255 } as { r: number; g: number; b: number });
    const [tickerData, setTickerData] = useState({} as any);
    const [t] = useTranslation();
    const propsData = props.data.data[0];
    const demoTitle = 'DEMO_DATA.DEMO_TITLE_LABEL';

    const abortController = new AbortController();

    useEffect(() => {
        let tempArray = [];
        if (props.data?.data?.length > 0 || '') {
            tempArray = props.data?.data?.filter((item) => item?.link?.length > 0); // check for other properties
        }
        if (tempArray.length > 0 || '') {
            setRequestInterval(1200); // reload interval in minutes
            setWidgetRssFeedConfig({
                divider: propsData.divider || '+++',
                limitFeeds: propsData.limitFeeds,
                limitFeedsAmount: propsData.limitFeedsAmount || 1,
                provideLink: propsData.provideLink || false,
                speed: propsData.speed || 1,
                teaserText: propsData.teaserText || false,
            } as WidgetEndpoint);
            setTickerTextSpeed((propsData.speed || 1) * 10);
            setBackgroundColor(props.data.backgroundColor);
            if (propsData.link?.length > 0 && propsData.applyInput) {
                setLink(propsData.link.toString());
                setRequestCount(requestCount + 1);
            }
        } else {
            setRequestCount(0);
        }

        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line
    }, [props.data]);

    useEffect(() => {
        const height = document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetHeight;
        if (height > 0) {
            setTickerFontSize(height < 50 ? '1.5rem' : height < 100 ? '2rem' : height < 150 ? '3rem' : height < 200 ? '4rem' : '5rem');
        }
        // eslint-disable-next-line
    }, [document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetHeight]);

    useEffect(() => {
        hex2rgb(backgroundColor);
        // eslint-disable-next-line
    }, [backgroundColor]);

    useEffect(() => {
        if (widgetRssFeedData.length > 0) {
            setTickerData(generateTickerData());
        }
        // eslint-disable-next-line
    }, [widgetRssFeedData]);

    useEffect(() => {
        if (requestCount > 0) {
            requestData({ signal: abortController.signal });
            const timeout = setTimeout(() => {
                setTriggerRequest(!triggerRequest);
            }, requestInterval);

            return () => {
                clearTimeout(timeout);
            };
        }
        // eslint-disable-next-line
    }, [triggerRequest, requestCount]);

    useEffect(() => { }, [isLoading, widgetRssFeedData]);

    async function requestData(options: any) {
        setIsLoading(true);
        const response = (await WidgetService.getRssFeed(link, options)) as any;
        if (response?.status === 200) {
            parseResponseToUsableObjects(response.data);
        } else {
            setWidgetRssFeedData([]);
        }
        setIsLoading(false);
    }

    function parseResponseToUsableObjects(response: any) {
        // process xml to usable items
        const items = new window.DOMParser().parseFromString(response, 'text/xml').querySelectorAll('item');
        let parsedData = [] as RssFeedItem[];
        if (items.length > 0) {
            items.forEach((item) => {
                parsedData.push({
                    description: `${item.querySelector('description').innerHTML}`,
                    link: `${item.querySelector('link').innerHTML}`,
                    title: `${item.querySelector('title').innerHTML}`,
                });
            });
            setWidgetRssFeedData(parsedData);
        } else {
            setWidgetRssFeedData([]);
        }
    }

    function openLink(e: any, link: string) {
        window.open(link);
    }

    function hex2rgb(hex) {
        var validHEXInput = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!validHEXInput) {
            return false;
        }
        var output = {
            r: parseInt(validHEXInput[1], 16),
            g: parseInt(validHEXInput[2], 16),
            b: parseInt(validHEXInput[3], 16),
        };
        setGradientColor(output);
    }

    function cleanFeedItem(item: RssFeedItem): RssFeedItem {
        const toLookFor = ['&amp;'];
        const toReplaceWith = ['&'];
        toLookFor.forEach((string, index) => {
            item.title = item.title.replace(string, toReplaceWith[index]);
            item.description = item.description.replace(string, toReplaceWith[index]);
        });
        return item;
    }

    function generateTickerData(): any {
        const amount = widgetRssFeedConfig.limitFeeds ? widgetRssFeedConfig.limitFeedsAmount : widgetRssFeedData.length;
        const data = widgetRssFeedData.slice(0, amount);
        if (!data[0].title) {
            return;
        }
        return data.reverse().map((item, index) => {
            if (item.title && item.description) {
                cleanFeedItem(item);
            }
            return (
                <div key={uuid.v4()} className={classes.tickerRow}>
                    &nbsp;&nbsp;{widgetRssFeedConfig.divider}&nbsp;&nbsp;
                    {widgetRssFeedConfig.provideLink ? (
                        <div onClick={(event) => openLink(event, item.link)}>
                            {item.title}
                            {widgetRssFeedConfig.teaserText ? `: ${item.description}` : null}
                        </div>
                    ) : (
                        <>
                            {item.title}
                            {widgetRssFeedConfig.teaserText ? `: ${item.description}` : null}
                        </>
                    )}
                </div>
            );
        });
    }

    return (
        <>
            {props?.data?.isUsingDefault ? (
                <DemoPreview title={demoTitle} isRss={true} />
            ) : requestCount === 0 && isLoading ? (
                <div className={classes.root} id="widget-content-height">
                    <BlockUi />
                </div>
            ) : (
                <>
                    {widgetRssFeedData.length > 0 && (
                        <div
                            className={classes.root}
                            id="widget-content-height"
                            style={{
                                color: props.data.fontColor,
                                width: '100%',
                                height: '100%',
                                fontSize: `${tickerFontSize}`,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Marquee
                                className={classes.marquee}
                                play={true}
                                pauseOnHover={true}
                                speed={tickerTextSpeed}
                                gradientWidth="0"
                            // gradientColor={[gradientColor.r, gradientColor.g, gradientColor.b]}
                            >
                                {tickerData.length > 0 && tickerData}
                            </Marquee>
                        </div>
                    )}
                    {isLoading && (
                        <div className={classes.blockUi}>
                            <BlockUi size={20} />
                        </div>
                    )}
                </>
            )}
            {link === '' && !props?.data?.isUsingDefault && (
                <div
                    className={classes.notConfigured}
                    style={{
                        color: props?.data.fontColor,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <div className={classes.notConfiguredIcon}>
                        <img src={not_configured_icon} alt="Not configured" />
                    </div>
                    {t('WIDGET_EDITOR.NOT_CONFIGURED_TEXT')}
                </div>
            )}
        </>
    );
}

export default RssFeed;
